import Vue from 'vue';
import { DeviceEdit, Device } from '@/interfaces/locations';
import { Commit } from 'vuex';

interface GatewayState {
	gateways: {
		[id: number]: Device;
	};
	selectedGateway: Device | null;
}

export default {
	namespaced: true,
	state: {
		gateways: [],
		selectedGateway: null,
	} as GatewayState,
	getters: {
		getGateways: (state: GatewayState): Device[] => {
			return Object.values(state.gateways);
		},

		getGateway:
			(state: GatewayState) =>
			(id: number): Device | undefined => {
				return state.gateways[id];
			},

		getSelectedGateway: (state: GatewayState): Device | null => {
			return state.selectedGateway;
		},
	},
	mutations: {
		SET_GATEWAYS(state: GatewayState, gateways: Device[]): void {
			const ret: { [id: number]: Device } = {};
			state.gateways = gateways.reduce((obj, gateway) => {
				obj[gateway.id] = gateway;
				return obj;
			}, ret);
		},

		ADD_GATEWAY(state: GatewayState, gateway: Device): void {
			Vue.set(state.gateways, gateway.id, gateway);
		},

		CHANGE_GATEWAY(state: GatewayState, gateway: Device & { id: number }): void {
			const oldGateway = state.gateways[gateway.id] || {};
			Vue.set(state.gateways, gateway.id, { ...oldGateway, ...gateway });
		},

		REMOVE_GATEWAY(state: GatewayState, gatewayId: number): void {
			Vue.delete(state.gateways, gatewayId);
		},

		CHANGE_SELECTED_GATEWAY(state: GatewayState, gateway: Device): void {
			state.selectedGateway = gateway;
		},

		CLEAN_GATEWAYS(state: GatewayState): void {
			state.gateways = {};
		},

		CLEAN_SELECTED_GATEWAY(state: GatewayState): void {
			state.selectedGateway = null;
		},
	},
	actions: {
		async fetchGateways({ commit }: { commit: Commit }): Promise<void> {
			const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/devices/gateways`);

			if (response && response.status === 200) {
				commit('SET_GATEWAYS', response.data.data);
			}
		},

		async fetchGateway({ commit }: { commit: Commit }, { id }: { id: number }): Promise<void> {
			const response = await Vue.axios.get(`${process.env.VUE_APP_API_URL}/devices/gateways/${id}`);
			if (response && response.status === 200) {
				commit('CHANGE_GATEWAY', response.data.data);
			}
		},

		async addGateway(
			{ commit }: { commit: Commit },
			{ payload }: { payload: DeviceEdit }
		): Promise<Device | undefined> {
			const response = await Vue.axios.post(
				`${process.env.VUE_APP_API_URL}/devices/gateways`,
				payload
			);
			if (response && response.status === 201) {
				commit('ADD_GATEWAY', response.data.data);
				return response.data.data;
			} else {
				return undefined;
			}
		},

		async modifyGateway(
			{ commit }: { commit: Commit },
			{ id, payload }: { id: number; payload: DeviceEdit }
		): Promise<Device | undefined> {
			const response = await Vue.axios.put(
				`${process.env.VUE_APP_API_URL}/devices/gateways/${id}`,
				payload
			);
			if (response && response.status === 200) {
				commit('CHANGE_GATEWAY', response.data.data);
				return response.data.data;
			} else {
				return undefined;
			}
		},

		async deleteGateway({ commit }: { commit: Commit }, { id }: { id: number }): Promise<void> {
			const response = await Vue.axios.delete(
				`${process.env.VUE_APP_API_URL}/devices/gateways/${id}`
			);
			if (response && response.status === 200) {
				commit('REMOVE_GATEWAY', id);
			}
		},

		selectGateway({ commit }: { commit: Commit }, { gateway }: { gateway: Device }): void {
			if (gateway) {
				commit('CHANGE_SELECTED_GATEWAY', gateway);
			}
		},

		cleanGateways({ commit }: { commit: Commit }): void {
			commit('CLEAN_GATEWAYS');
		},

		cleanSelectedGateway({ commit }: { commit: Commit }): void {
			commit('CLEAN_SELECTED_GATEWAY');
		},

		cleanAllGateways({ commit }: { commit: Commit }): void {
			commit('CLEAN_GATEWAYS');
			commit('CLEAN_SELECTED_GATEWAY');
		},
	},
};
